/**
 * @packageDocumentation
 * Translates numeric server identifiers into card symbol specifications, wiring animation assets for civilians, superheroes, and wildcards.
 */
// loadCards.ts
import * as PIXI from "pixi.js";
import type { SymbolSpec } from "./CardScreen";
import { RoundTuple } from "./server";

// === Config you may tweak to match your loader aliases ===
const BACK_ALIAS = "backcard"; // closed-side texture (replace with yours)
const civilian_wild_card = "civilian_wild_card"; // wildcard texture (replace with yours)
const hero_wild_card = "hero_wild_card"; // wildcard texture (replace with yours)

const COLORS = ["Blue", "Green", "Purple", "Red", "Yellow"] as const;
type Color = typeof COLORS[number];

type Tier = "civilian" | "superhero";

const FRAME_BY_BORDER: Record<1|2|3|4, string | undefined> = {
  1: "bronze_frame_anim",                   // No border
  2: "Silver_frame_anim",         // plays when revealed
  3: "Gold_frame_anim",
  4: "Hologram_frame_anim",
};

// default scales/offsets to preserve your current look
const DEFAULT_SCALES = {
  civilian: 1,
  superhero: 1,
  frame:    1.00,
};
const DEFAULT_OFFSETS = {
  spineY: undefined as number | undefined, // keep CardScreen default if undefined
  frameY: 0,
};

// Build asset keys from color+tier.
// Example: Blue_civilian_json / Blue_civilian_atlas; animation name "animation".
function characterKeys(color: Color, tier: Tier) {
  const base = `${color}_${tier}`;
  return {
    json:  `${base}_json`,
    atlas: `${base}_atlas`,
    anim:  "idle_anim",
  };
}

function frameKeys(frameAlias: string) {
  return {
    json: `${frameAlias}_json`,
    atlas: `${frameAlias}_atlas`,
    anim:  "idle_anim",
  };
}

// Decode 1..42 into (tier, color, border)
function decode(id: number): { kind: "wildcard1" } | { kind: "wildcard2" } | {
  kind: "normal",
  tier: Tier,
  color: Color,
  border: 1 | 2 | 3 | 4
} {
  if (id === 41) return { kind: "wildcard1" };
  if (id === 42) return { kind: "wildcard2" };

  const idx0 = id - 1;                 // 0..39
  const group = Math.floor(idx0 / 5);  // 0..7 => 4 regular + 4 superhero blocks
  const cIdx  = idx0 % 5;              // color index 0..4

  const tier: Tier = group < 4 ? "civilian" : "superhero";
  const border = (group % 4) + 1 as 1 | 2 | 3 | 4;
  const color = COLORS[cIdx];

  return { kind: "normal", tier, color, border };
}

export function specFromId(id: number): SymbolSpec {
  const backTexture = PIXI.Texture.from(BACK_ALIAS);
  const decoded = decode(id);

  if (decoded.kind === "wildcard1") {
    // For now: revealed side is the same back image (static). Replace if you add a wildcard spine.
    return {
      backTexture,
      frontSpriteTexture: PIXI.Texture.from(civilian_wild_card),
      frontScale: 1.0,
      frontY: 0,
    };
  }else
  if (decoded.kind === "wildcard2") {
    // For now: revealed side is the same back image (static). Replace if you add a wildcard spine.
    return {
      backTexture,
      frontSpriteTexture: PIXI.Texture.from(hero_wild_card),
      frontScale: 1.0,
      frontY: 0,
    };
  }

  // Character spine
  const { tier, color, border } = decoded;
  const char = characterKeys(color, tier);
  const frameAlias = FRAME_BY_BORDER[border];

  const spec: SymbolSpec = {
    backTexture,
    spineJsonKey: char.json,
    spineAtlasKey: char.atlas,
    anim: char.anim,
    spineScale: DEFAULT_SCALES[tier],
    spineY: DEFAULT_OFFSETS.spineY,
  };

  // Optional frame overlay
  if (frameAlias) {
    const fr = frameKeys(frameAlias);
    spec.frameJsonKey = fr.json;
    spec.frameAtlasKey = fr.atlas;
    spec.frameAnim = fr.anim;
    spec.frameScale = DEFAULT_SCALES.frame;
    spec.frameY = DEFAULT_OFFSETS.frameY;
  }

  return spec;
}

/** Build 3 specs from the server tuple [id1,id2,id3,bonus] */
export function specsFromRoundTuple(tuple: RoundTuple): SymbolSpec[] {
  const [a,b,c] = tuple;
  return [specFromId(a), specFromId(b), specFromId(c)];
}
